/**
 * Transition Proposal show — `[PROPOSED]` Inspector-grammar gate at
 * `/transition-proposals/:id`. Renders the full grammar shell with the named
 * primary actions **Approve / Reject** present (the load-bearing human gate,
 * `frontend-entity-coverage §2.3` / `frontend-display §7.2`).
 *
 * The actions route through the sanctioned worker write path
 * (`POST /api/transition-proposals/:id/approve|/reject`, `11 §3.8`) via
 * `useApiAction`. Until that endpoint ships the call degrades gracefully — the
 * error toast is informative and the page never crashes — and the relations /
 * overview show the §5.5 labeled-empty teaser.
 */
import { useParams } from "react-router-dom";
import { Check, X } from "@/components/ui/icon";

import { Button } from "@/components/ui/button";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RelationsBlock,
  AuditBlock,
  TechnicalId,
} from "@/components/inspector";
import { ProposedShowTeaser, BackToList } from "../_shared/ProposedModule";
import { useApiAction } from "../_shared/useApiAction";

export function TransitionProposalShow() {
  const { id } = useParams<{ id: string }>();
  const { run, loading } = useApiAction();

  const approve = () => {
    if (!id) return;
    void run(`/transition-proposals/${id}/approve`, {
      invalidateResources: ["transition-proposals", "events", "artifacts"],
      successMessage: "Transition approved.",
    });
  };
  const reject = () => {
    if (!id) return;
    void run(`/transition-proposals/${id}/reject`, {
      invalidateResources: ["transition-proposals", "events", "artifacts"],
      successMessage: "Transition rejected.",
    });
  };

  return (
    <Inspector>
      <InspectorHeader
        title="Transition Proposal"
        subtitle="A proposed phase transition awaiting human approval."
        status="proposed"
        primaryAction={
          <Button
            size="sm"
            onClick={approve}
            disabled={loading}
            data-testid="transition-proposal-approve"
          >
            <Check className="mr-2 h-4 w-4" /> Approve
          </Button>
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={reject}
              disabled={loading}
              data-testid="transition-proposal-reject"
            >
              <X className="mr-2 h-4 w-4" /> Reject
            </Button>
            <BackToList to="/transition-proposals" label="Proposals" />
          </>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Artifact" />
          <Field label="Contour" />
          <Field label="From phase" />
          <Field label="To phase" />
          <Field label="Proposed by" />
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Body">
        <ProposedShowTeaser endpoint="/api/transition-proposals" />
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              emptyMessage:
                "Artifact · Contour · from/to Phase · proposing AgentRun appear once this ships.",
              items: [],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={String(id)} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="transition-proposals" entityId={id} />
      </InspectorSection>
    </Inspector>
  );
}

export default TransitionProposalShow;
